/**
 * One recording of a voice sample (ADR-0062), without the browser: the questioning's
 * `RecorderController` on a device that is handed in, with the speech meter on its level. The
 * speech is counted while it records; the recording ends by itself once it holds the speech asked
 * for, or at its longest length, whatever was said. The operator may end it early, or start over:
 * what was being recorded is then dropped and a fresh recording starts in the same tap.
 *
 * Where the device reads the true level (`RecorderDevice.signal`), a recording begins with a
 * check of the room: for about two seconds nothing is counted and the room is listened to
 * (`room-check.ts`). The recording runs from the moment the microphone opens, so the file begins
 * with the room's own sound. A quiet or a noisy room starts the count by itself; in one that is
 * too loud the session waits — the operator starts over elsewhere, or goes on (`goOn`). While it
 * counts, the same level says when the voice is too faint or the sound clips
 * (`voice-guidance.ts`). A device without that reading is counted from the start, as before.
 *
 * It says two things apart, as the recorder does: the phase, which changes a few times, and the
 * readout — the counted speech, the level — which moves many times a second.
 */
import { nextAnnouncement } from '@/components/humint/voice/voice-format'
import {
  VOICE_SAMPLE_MAX_ELAPSED_MS,
  VOICE_SAMPLE_TARGET_SPEECH_MS,
} from '@/components/humint/voice/voice-rules'
import {
  IDLE_VOICE_READOUT,
  type VoiceAnnounced,
  type VoiceCounting,
  type VoiceMicrophoneProblem,
  type VoiceReadout,
  type VoiceRoom,
  type VoiceTakeEnding,
} from '@/components/humint/voice/voice-types'
import type { LevelSignal } from '../recording/level-meter'
import {
  IDLE_RECORDER_READOUT,
  type RecordedAudio,
  RecorderController,
  type RecorderDevice,
  type RecorderReadout,
  type RecorderView,
} from '../recording/recorder-controller'
import {
  advanceRoomCheck,
  isRoomChecked,
  NEW_ROOM_CHECK,
  type RoomCheck,
  type RoomReading,
  type RoomSample,
  readRoomCheck,
  roomCheckShare,
} from './room-check'
import {
  advanceSpeechMeter,
  meterInRoom,
  readSpeechMeter,
  SILENT_METER,
  type SpeechMeter,
} from './speech-meter'
import {
  advanceGuidance,
  type GuidanceTold,
  type GuidanceTrack,
  NO_GUIDANCE,
  silenceToTell,
  tellsGuidance,
} from './voice-guidance'

/** How often the speech is counted: finer than the questioning's meter moves. */
export const VOICE_TICK_MS = 100

/** How many bars the review's player draws of a sample. */
export const VOICE_WAVEFORM_BARS = 64

export interface VoiceSampleSessionDeps {
  /** Ask for the microphone; rejects when it is refused or cannot be started. */
  open: () => Promise<RecorderDevice>
  /** A monotonic clock, in ms. */
  now: () => number
  /** The wall clock, ISO. */
  isoNow: () => string
  /** The recording ends on its own once its slices add up to this many bytes. */
  maxBytes: number
  /** The speech asked for; `VOICE_SAMPLE_TARGET_SPEECH_MS` unless a test asks for less. */
  targetSpeechMs?: number
  /** The longest it records; `VOICE_SAMPLE_MAX_ELAPSED_MS` by default. */
  maxElapsedMs?: number
  /** `VOICE_TICK_MS` by default. */
  tickMs?: number
  /** For how long the room is listened to before the count; `VOICE_ROOM_CHECK_MS` by default. */
  roomCheckMs?: number
}

export interface VoiceSessionView {
  /**
   * `checking`: the microphone is open and the recording runs, the room is listened to and
   * nothing is counted yet. `recording`: the speech is counted.
   */
  phase: 'idle' | 'starting' | 'checking' | 'recording'
  /** Why the last start did not record. */
  problem: VoiceMicrophoneProblem | null
  /**
   * While `checking`: `loud` once the room was found too loud — the session waits for `goOn` or a
   * start-over; absent while it is still listened to. While `recording`: what the check found,
   * null where it could not tell. Absent where no check was made: the device reads no true level.
   */
  room?: VoiceRoom | null
}

export const IDLE_VOICE_SESSION_VIEW: VoiceSessionView = { phase: 'idle', problem: null }

/** A recording that has ended, with the speech counted in it. */
export interface VoiceSample {
  audio: RecordedAudio
  /** Net speech counted, never more than was recorded. */
  speechMs: number
  counting: VoiceCounting
  ending: VoiceTakeEnding
  /** The level over the recording, 0..1 per bar. */
  waveform: readonly number[]
}

export interface VoiceSampleSessionListeners {
  /** It is asking for the microphone, it records, or a start gave no recording. */
  onView: (view: VoiceSessionView) => void
  /** The counted speech and the level moved: every tick while it records. */
  onReadout: (readout: VoiceReadout) => void
  /** The recording has ended, by itself or asked to: the session is idle again. */
  onSampled: (sample: VoiceSample) => void
}

/** The loudest level of each of `bars` equal stretches of a recording; silence where it has none. */
export function waveformOfLevels(
  levels: readonly number[],
  bars: number = VOICE_WAVEFORM_BARS
): number[] {
  return Array.from({ length: bars }, (_, bar) => {
    const from = Math.floor((bar * levels.length) / bars)
    const to = Math.max(from + 1, Math.floor(((bar + 1) * levels.length) / bars))
    return levels.slice(from, to).reduce((peak, level) => Math.max(peak, level), 0)
  })
}

/** What one recording has heard so far. */
interface Heard {
  /**
   * `checking`: the room is listened to. `held`: it was found too loud, and the count waits for
   * the operator. `counting`: the speech is counted.
   */
  stage: 'checking' | 'held' | 'counting'
  check: RoomCheck
  /** What the check found; null before it ended, and where none was made. */
  room: RoomReading | null
  /** The recorded time at which the count began. */
  countFromMs: number
  meter: SpeechMeter
  guidance: GuidanceTrack
  /** The guidance a screen reader was last told. */
  told: GuidanceTold | null
  /** Every level, in order: the room check's too. */
  levels: number[]
  /** The recorder's readout the last level came with. */
  last: RecorderReadout
  /** What a screen reader was last told. */
  announced: VoiceAnnounced | null
  /** Why this session is ending it, when it is. */
  ending: 'target' | 'cap' | 'stopped' | null
}

const nothingHeard = (): Heard => ({
  stage: 'counting',
  check: NEW_ROOM_CHECK,
  room: null,
  countFromMs: 0,
  meter: SILENT_METER,
  guidance: NO_GUIDANCE,
  told: null,
  levels: [],
  last: IDLE_RECORDER_READOUT,
  announced: null,
  ending: null,
})

export class VoiceSampleSession {
  private controller: RecorderController
  private heard = nothingHeard()
  private phase: VoiceSessionView['phase'] = 'idle'
  private disposed = false

  constructor(
    private readonly deps: VoiceSampleSessionDeps,
    private readonly listeners: VoiceSampleSessionListeners
  ) {
    this.controller = this.newController()
  }

  /** Ask for the microphone and record. Call it inside the tap. */
  async start(): Promise<void> {
    if (this.disposed) return
    await this.controller.start()
  }

  /**
   * End the recording early; `onSampled` follows with what was recorded. Nothing ends while the
   * room is still checked: there is nothing of the subject in it yet.
   */
  stop(): void {
    if (this.phase === 'recording') this.end('stopped')
  }

  /** The room is too loud and the operator records all the same: the count starts. */
  goOn(): void {
    const { heard } = this
    if (this.disposed || this.phase !== 'checking' || heard.stage !== 'held' || heard.ending) return
    this.beginCounting(heard.last.elapsedMs)
  }

  /**
   * Drop what is being recorded and record from zero. The microphone is let go and asked for
   * again at once, so call it inside the tap.
   */
  async startOver(): Promise<void> {
    if (this.disposed) return
    this.controller.dispose()
    this.controller = this.newController()
    this.phase = 'idle'
    await this.controller.start()
  }

  /** The dialog goes: the microphone is let go and nothing more is said. */
  dispose(): void {
    this.disposed = true
    this.controller.dispose()
  }

  private newController(): RecorderController {
    const { open, now, isoNow, maxBytes, tickMs = VOICE_TICK_MS } = this.deps
    // A controller that was replaced by a start-over has nothing more to say.
    const current = () => this.controller === controller
    const opening = async (): Promise<RecorderDevice> => {
      const device = await open()
      // A device that reads the true level has its room listened to before anything is counted.
      if (current()) this.heard.stage = device.signal ? 'checking' : 'counting'
      return device
    }
    const controller: RecorderController = new RecorderController(
      { open: opening, now, isoNow, maxBytes, tickMs },
      {
        onView: view => {
          if (current()) this.show(view)
        },
        onReadout: readout => {
          if (current()) this.read(readout)
        },
        onRecorded: audio => {
          if (current()) this.finish(audio)
        },
      }
    )
    return controller
  }

  /** The microphone is open: the room is checked, or the speech counted. */
  private isOpen(): boolean {
    return this.phase === 'checking' || this.phase === 'recording'
  }

  private end(ending: 'target' | 'cap' | 'stopped'): void {
    if (!this.isOpen() || this.heard.ending) return
    this.heard.ending = ending
    this.controller.stop()
  }

  private show(view: RecorderView): void {
    // The session never pauses, so the recorder is never `paused`.
    const open = this.heard.stage === 'counting' ? 'recording' : 'checking'
    const phase = view.phase === 'recording' || view.phase === 'paused' ? open : view.phase
    const ended = this.isOpen() && phase === 'idle'
    this.phase = phase
    // A fresh recording counts from zero, and says so before the microphone has opened: the
    // count of the recording before it is not this one's.
    if (phase === 'starting') {
      this.heard = nothingHeard()
      this.listeners.onReadout(IDLE_VOICE_READOUT)
    }
    // A recording that ended says so with its sample (`onSampled`), which follows at once.
    if (!ended) this.listeners.onView(this.viewOf(view.problem))
  }

  /** The phase as it is said, with what the room check has found so far. */
  private viewOf(problem: VoiceSessionView['problem']): VoiceSessionView {
    const { phase } = this
    const { stage, room } = this.heard
    if (phase === 'checking' && stage === 'held') return { phase, problem, room: 'loud' }
    if (phase === 'recording' && room) return { phase, problem, room: room.room }
    return { phase, problem }
  }

  private read(readout: RecorderReadout): void {
    // The recorder's own reset, at its start and its end: the count stands until the next start.
    if (readout === IDLE_RECORDER_READOUT) return
    const { heard } = this
    // Only a tick brings a new level: the end of a recording says the time again, no level.
    if (readout.levels !== heard.last.levels) this.hear(readout)
    heard.last = readout
    this.listeners.onReadout(this.readoutOf(readout.elapsedMs))
    this.endWhenFull(readout.elapsedMs)
  }

  /** One more level: it is the room's while the room is checked, and counted after. */
  private hear(readout: RecorderReadout): void {
    const { heard } = this
    const level = readout.levels[readout.levels.length - 1] ?? 0
    const dtMs = readout.elapsedMs - heard.last.elapsedMs
    const signal = readout.signal ?? null
    heard.levels.push(level)
    if (heard.stage === 'checking') {
      this.checkRoom({ level, dbfs: signal?.dbfs ?? null, dtMs }, readout.elapsedMs)
    } else if (heard.stage === 'counting') {
      this.count({ level, dtMs }, signal, readout.elapsedMs)
    }
  }

  /** The room after one more level: once it was listened to, the count starts — or waits. */
  private checkRoom(sample: RoomSample, elapsedMs: number): void {
    const { heard } = this
    heard.check = advanceRoomCheck(heard.check, sample)
    if (!isRoomChecked(heard.check, this.deps.roomCheckMs)) return
    heard.room = readRoomCheck(heard.check)
    if (heard.room.room !== 'loud') {
      this.beginCounting(elapsedMs)
      return
    }
    // Too loud: the operator finds a quieter place and starts over, or goes on all the same.
    heard.stage = 'held'
    this.listeners.onView(this.viewOf(null))
  }

  /** The speech is counted from here, against the room the check measured. */
  private beginCounting(elapsedMs: number): void {
    const { heard } = this
    heard.stage = 'counting'
    heard.countFromMs = elapsedMs
    heard.meter = meterInRoom(heard.room?.floor ?? null)
    this.phase = 'recording'
    this.listeners.onView(this.viewOf(null))
  }

  private count(
    sample: { level: number; dtMs: number },
    signal: LevelSignal | null,
    elapsedMs: number
  ): void {
    const { heard } = this
    heard.meter = advanceSpeechMeter(heard.meter, sample)
    if (signal) {
      const frame = { ...signal, dtMs: sample.dtMs }
      heard.guidance = advanceGuidance(heard.guidance, frame, heard.room?.noiseDbfs ?? null)
    }
    this.tell(signal !== null, elapsedMs)
  }

  /** What a screen reader is told after this level: far less often than the caption changes. */
  private tell(measures: boolean, elapsedMs: number): void {
    const { heard } = this
    const { shown } = heard.guidance
    const tells = tellsGuidance(shown, heard.announced, heard.told, elapsedMs)
    if (tells && shown) heard.told = { guidance: shown, atMs: elapsedMs }
    const reading = readSpeechMeter(heard.meter)
    // Where the true level is read, a voice too faint to count is no silence to be told.
    const silentMs = measures ? silenceToTell(reading.silentMs, heard.guidance) : reading.silentMs
    const hearing = { ...reading, silentMs, guidance: tells ? shown : null }
    heard.announced = nextAnnouncement(heard.announced, hearing)
  }

  private readoutOf(elapsedMs: number): VoiceReadout {
    const { stage, check, countFromMs, meter, levels, guidance, announced } = this.heard
    const level = levels[levels.length - 1] ?? 0
    if (stage !== 'counting') {
      const roomShare = stage === 'held' ? 1 : roomCheckShare(check, this.deps.roomCheckMs)
      return { ...IDLE_VOICE_READOUT, elapsedMs, level, roomShare }
    }
    const target = this.deps.targetSpeechMs ?? VOICE_SAMPLE_TARGET_SPEECH_MS
    const { speechMs, speaking, counting } = readSpeechMeter(meter)
    return {
      elapsedMs,
      talkMs: Math.max(0, elapsedMs - countFromMs),
      // The count never runs past what was asked for.
      speechMs: Math.min(speechMs, target),
      speaking,
      counting,
      level,
      roomShare: null,
      guidance: guidance.shown,
      announced,
    }
  }

  /** The recording ends by itself: full of speech, or at its longest — the room check counts in. */
  private endWhenFull(elapsedMs: number): void {
    const { stage, meter } = this.heard
    const target = this.deps.targetSpeechMs ?? VOICE_SAMPLE_TARGET_SPEECH_MS
    const longest = this.deps.maxElapsedMs ?? VOICE_SAMPLE_MAX_ELAPSED_MS
    if (stage === 'counting' && readSpeechMeter(meter).speechMs >= target) this.end('target')
    else if (elapsedMs >= longest) this.end('cap')
  }

  private finish(audio: RecordedAudio): void {
    const { heard } = this
    const reading = this.readoutOf(audio.durationMs)
    this.listeners.onSampled({
      audio,
      speechMs: Math.min(reading.speechMs, audio.durationMs),
      counting: reading.counting,
      // Nobody asked it to end: the microphone went away, or the recorder failed.
      ending: audio.interrupted ? 'interrupted' : (heard.ending ?? 'stopped'),
      waveform: waveformOfLevels(heard.levels),
    })
  }
}
